import { toast } from '@heidi/design-system/toast';
import { getDefaultStore } from 'jotai';
import { Info } from 'lucide-react';
import { createElement } from 'react';

import { voiceAssistantMessages } from '../components/voice-assistant.messages';
import { voiceActiveTabAtom } from '../store/voiceActiveTabAtom';
import { openChooseStructurePicker } from './openChooseStructurePicker';
import type { VoiceUiActionsPort } from './voice-ui-actions.port';

const DOCUMENT_TEMPLATE_ROW =
  '[data-testid="v2-note-context-card-document-template-row"]';

/**
 * Open the template / structure picker for the active tab.
 * - Note (and other non-document tabs): choose-structure modal atom.
 * - Document tab: click the document template row (local picker).
 *
 * Minimizes the voice workspace when needed so the picker is not buried
 * under the z-90 voice overlay / dock.
 *
 * Honest-failure contract: returns `false` (and surfaces a toast so the
 * clinician knows why nothing happened) when the document picker's target row
 * is not on screen. Callers relay the boolean to the agent so it stops
 * claiming success when nothing actually rendered (AGE-1618).
 */
export const openTemplatePicker = (port: VoiceUiActionsPort): boolean => {
  const tab = getDefaultStore().get(voiceActiveTabAtom);
  if (tab?.tabKind === 'document') {
    port.minimizeVoiceWorkspace();
    const el = document.querySelector(DOCUMENT_TEMPLATE_ROW);
    if (el instanceof HTMLElement) {
      el.click();
      return true;
    }
    toast({
      tone: 'subtle',
      icon: createElement(Info),
      title: port.formatMessage(voiceAssistantMessages.templatePickerUnavailable),
    });
    return false;
  }
  openChooseStructurePicker(port);
  return true;
};
